import React from "react";
import Hero from "@insightfulscience/atomic-react/Hero";
import { useLocale } from "@insightfulscience/smart-react/i18";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import HeroBackground from "@insightfulscience/atomic-react/Hero/HeroBackground";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import LogoCircle from "@insightfulscience/atomic-react/LogoCircle";
import CardBlock from "@insightfulscience/atomic-react/CardBlock";
import { CardBlockRow, CardBlockCol } from "@insightfulscience/atomic-react/CardBlock/Grid";
import Section from "@insightfulscience/atomic-react/Section";
import Heading from "@insightfulscience/atomic-react/Heading";
import SectionTryForFree from "../../SectionTryForFree";
import SiteLayout from "../../SiteLayout";
import Testimonials from "./Testimonials";
import PreloadImages from "../../PreloadImages";

const tiles = [
	{
		title: "TILE_1_TITLE",
		description: "TILE_1_DESCRIPTION",
		img: "TILE_1_IMAGE",
		cardType: "TILE_1_TYPE",
		linkButtonText: "TILE_1_LINK",
		href: "TILE_1_LINK_HREF",
	},
	{
		title: "TILE_2_TITLE",
		description: "TILE_2_DESCRIPTION",
		img: "TILE_2_IMAGE",
		cardType: "TILE_2_TYPE",
		linkButtonText: "TILE_2_LINK",
		href: "TILE_2_LINK_HREF",
	},
];

const testimonials1 = [
	{
		colorLogo: "TESTIMONIAL_LOGO_1",
		monochrome: "TESTIMONIAL_LOGO_1",
		author: "TESTIMONIAL_AUTHOR_1",
		company: "TESTIMONIAL_COMPANY_1",
		text: "TESTIMONIAL_TEXT_1",
	},
	{
		colorLogo: "TESTIMONIAL_LOGO_2",
		monochrome: "TESTIMONIAL_LOGO_2",
		author: "TESTIMONIAL_AUTHOR_2",
		company: "TESTIMONIAL_COMPANY_2",
		text: "TESTIMONIAL_TEXT_2",
	},
	{
		colorLogo: "TESTIMONIAL_LOGO_3",
		monochrome: "TESTIMONIAL_LOGO_3",
		author: "TESTIMONIAL_AUTHOR_3",
		company: "TESTIMONIAL_COMPANY_3",
		text: "TESTIMONIAL_TEXT_3",
	},
	{
		colorLogo: "TESTIMONIAL_LOGO_4",
		monochrome: "TESTIMONIAL_LOGO_4",
		author: "TESTIMONIAL_AUTHOR_4",
		company: "TESTIMONIAL_COMPANY_4",
		text: "TESTIMONIAL_TEXT_4",
	},
	{
		colorLogo: "TESTIMONIAL_LOGO_5",
		monochrome: "TESTIMONIAL_LOGO_5",
		author: "TESTIMONIAL_AUTHOR_5",
		company: "TESTIMONIAL_COMPANY_5",
		text: "TESTIMONIAL_TEXT_5",
	},
];

const testimonials2 = [
	{
		colorLogo: "TESTIMONIAL_LOGO_6",
		monochrome: "TESTIMONIAL_LOGO_6",
		author: "TESTIMONIAL_AUTHOR_6",
		company: "TESTIMONIAL_COMPANY_6",
		text: "TESTIMONIAL_TEXT_6",
	},
	{
		colorLogo: "TESTIMONIAL_LOGO_7",
		monochrome: "TESTIMONIAL_LOGO_7",
		author: "TESTIMONIAL_AUTHOR_7",
		company: "TESTIMONIAL_COMPANY_7",
		text: "TESTIMONIAL_TEXT_7",
	},
];

const Customers = () => {
	const [t] = useLocale("customers-page");

	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.OGP title={t("META.TITLE")} description={t("META.DESCRIPTION")} />
				<PageMeta.Canonical href={t("META.CANONICAL")} />
				<PreloadImages
					imagesHref={[
						"/images/dotmatics/lab-archives/about/cookie-2.png",
						"/images/geneious/customers/customers-hero-circles.png",
						"/images/common/companies/abbvie-logo.svg",
						"/images/common/companies/inserm-logo.svg",
						"/images/common/companies/roche-logo.svg",
						"/images/common/companies/sanofi-logo.svg",
						"/images/common/companies/dkfz-logo.svg",
						"/images/common/companies/corteva-logo.svg",
					]}
				/>
			</PageMeta>

			<HeroBackground.Images
				color="4"
				images={[t("HERO_IMAGE_1"), t("HERO_IMAGE_2")]}
				imagePositions={["left center", "100% center"]}
			>
				<Hero sx={{ textAlign: "left" }} color="transparent" spacing={{ py: 18 }}>
					<HeroRow mx center={false} utility={{ align: { items: "center" } }}>
						<HeroCol md={12} lg={7} smOrder={1} mdOrder={0}>
							<HeroTitle
								spacing={{ sm: { mt: 6 }, md: { mt: 0 } }}
								sx={{ fontColor: "black", textAlign: "left" }}
							>
								{t("HERO_TITLE")}
							</HeroTitle>
							<HeroDescription sx={{ fontColor: "black", textAlign: "left" }}>
								{t("HERO_DESCRIPTION")}
							</HeroDescription>
						</HeroCol>
					</HeroRow>
				</Hero>
			</HeroBackground.Images>
			<Section spacing={{ pt: 20 }}>
				<Container>
					<Row>
						<Col>
							<Heading Tag="h2" sx={{ fontSize: "xl" }}>
								{t("GENEIOUS_PRIME_TITLE")}
							</Heading>
						</Col>
					</Row>
					<Row>
						<Col lg={7} md={12} sm={12}>
							<Paragraph sx={{ fontSize: "l" }}>{t("GENEIOUS_PRIME_DESCRIPTION")}</Paragraph>
						</Col>
					</Row>
				</Container>
			</Section>
			<Section spacing={{ py: 10 }}>
				<Container>
					<Row mx my>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_1")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_2")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_3")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_4")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_5")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_6")} />
						</Col>
					</Row>
					<Row mx my center>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_7")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_8")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_9")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_10")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_11")} />
						</Col>
					</Row>
					<Row mx my>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_12")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_13")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_14")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_15")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_16")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_17")} />
						</Col>
					</Row>
				</Container>
			</Section>
			<Section spacing={{ py: 10 }}>
				<Container>
					<Testimonials testimonials={testimonials1} />
				</Container>
			</Section>
			<Section spacing={{ pt: 10 }}>
				<Container>
					<Row>
						<Col>
							<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }}>
								{t("HELP__CUSTOMERS_TITLE")}
							</Heading>
							<Paragraph sx={{ fontSize: "l", textAlign: "center", fontColor: "black--custom-1" }}>
								{t("HELP__CUSTOMERS_DESCRIPTION")}
							</Paragraph>
						</Col>
					</Row>
				</Container>
			</Section>
			<Section spacing={{ pb: 10 }}>
				<Container>
					<CardBlockRow>
						{tiles.map(item => (
							<CardBlockCol key={item.title} lg={6} md={6} sm={12}>
								<CardBlock
									isBaseUrl
									isImgFluid
									titleText={t(item.title)}
									href={t(item.href)}
									cardType={t(item.cardType)}
									descriptionText={t(item.description)}
									imgSrc={t(item.img)}
									linkButtonText={t(item.linkButtonText)}
								/>
							</CardBlockCol>
						))}
					</CardBlockRow>
				</Container>
			</Section>
			<Section spacing={{ pt: 10 }}>
				<Container>
					<Row>
						<Col>
							<Heading Tag="h2" sx={{ fontSize: "xl" }}>
								{t("GENEIOUS_BIOLOGICS_TITLE")}
							</Heading>
						</Col>
					</Row>
					<Row>
						<Col lg={7} md={12} sm={12}>
							<Paragraph sx={{ fontSize: "l" }}>{t("GENEIOUS_BIOLOGICS_DESCRIPTION")}</Paragraph>
						</Col>
					</Row>
				</Container>
			</Section>
			<Section spacing={{ py: 10 }}>
				<Container>
					<Row mx my>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_18")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_19")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_20")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_21")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_22")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_23")} />
						</Col>
					</Row>
					<Row mx my center>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_24")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_25")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_26")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_27")} />
						</Col>
						<Col md={2} sm={4} sx={{ textAlign: "center" }}>
							<LogoCircle wrapperUtility={{ bgColor: "4" }} imgSrc={t("COMPANY_LOGO_28")} />
						</Col>
					</Row>
				</Container>
			</Section>
			<Section spacing={{ pt: 10, pb: 20 }}>
				<Container>
					<Testimonials testimonials={testimonials2} />
				</Container>
			</Section>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default Customers;
